/* eslint-disable react/require-default-props */
import React, { useCallback, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  ModalButtons,
  Button,
} from 'pvs-design-system';
import {
  useGetRootLocationsQuery,
  useLazyGetRootChildLocationsQuery,
} from 'api';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import type { RootLocationParam } from 'components/ToolDeploymentSection/ToolDeploymentSection';
import { BranchLocation, Location } from 'components/LocationTree/types';
import LocationTree from 'components/LocationTree/LocationTree';

interface LocationSelectionModalProps {
  isOpen: boolean;
  onConfirm: (selectedLocations: RootLocationParam[]) => void;
  onClose: () => void;
  existingLocations?: RootLocationParam[]; // Optional prop to pass selected locations
}

const LocationSelectionModal: React.FC<LocationSelectionModalProps> = ({
  isOpen,
  onConfirm,
  onClose,
  existingLocations = [],
}) => {
  const {
    data: rootLocations,
    isLoading,
    isError,
    isFetching,
  } = useGetRootLocationsQuery();

  const [fetchChildLocations] = useLazyGetRootChildLocationsQuery();

  const loadChildren = useCallback(
    async (branch: BranchLocation): Promise<Location[]> => {
      const children = await fetchChildLocations(branch.id).unwrap();

      return children;
    },
    [fetchChildLocations]
  );

  const [selectedLocations, setSelectedLocations] =
    useState<RootLocationParam[]>(existingLocations);

  const handleSelectedChange = (location: Location) => {
    setSelectedLocations((prevSelectedLocations) => {
      let newSelectedLocations: RootLocationParam[] = [
        ...prevSelectedLocations,
      ];

      if (newSelectedLocations.some((loc) => loc.id === location.id)) {
        newSelectedLocations = newSelectedLocations.filter(
          (loc) => loc.id !== location.id
        );
      } else {
        newSelectedLocations.push(location as RootLocationParam);
      }
      return newSelectedLocations;
    });
  };

  return (
    <Modal onClose={onClose} open={isOpen} closeModalOnBackdropClick fullWidth>
      <ModalHeader title="Add Location Root" onClose={onClose} description="" />
      <ModalContent sx={{ overflowX: 'hidden' }}>
        <LoadingSpinner
          displayText="Loading Root Locations"
          showLoading={isLoading}
        />

        {!isFetching && !isError && rootLocations && (
          <LocationTree
            initialTree={rootLocations}
            loadChildren={loadChildren}
            selectedLocations={selectedLocations as Location[]}
            onSelectedChange={handleSelectedChange}
          />
        )}
      </ModalContent>
      <ModalFooter>
        <ModalButtons>
          <Button onClick={() => onClose()} color="secondary">
            Cancel
          </Button>
          <Button onClick={() => onConfirm(selectedLocations)}>
            Add Selected
          </Button>
        </ModalButtons>
      </ModalFooter>
    </Modal>
  );
};

export default LocationSelectionModal;
